<template>
  <Teleport to="body">
    <Transition name="fade">
      <div v-if="active" ref="popup" class="popup">
        <div class="popup-inner">
          <div ref="popupContent" class="content">
            <div class="content-bg-top">
              <ChocolateIcon class="icon1" />
              <StripedChocolateIcon class="icon2" />
              <div class="bg1" />
              <div class="bg2" />
              <div class="bg3" />
            </div>
            <div class="content-bg-bottom">
              <ChocolateHeartIcon class="icon1" />
              <CookieIcon class="icon2" />
              <div class="bg1" />
              <div class="bg2" />
              <div class="bg3" />
              <div class="bg4" />
            </div>
            <div class="text">
              <h3 class="title">{{ t('form.title-success') }}</h3>
              <p class="description">{{ t('form.description-success') }}</p>
            </div>
          </div>
          <button class="close" @click="close">
            <CloseIcon class="close-icon" />
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script lang="ts" setup>
import CloseIcon from '@/assets/icons/close.svg?component'
import ChocolateIcon from '@/assets/icons/tiles/chocolate.svg'
import ChocolateHeartIcon from '@/assets/icons/tiles/chocolate_heart.svg'
import CookieIcon from '@/assets/icons/tiles/cookie.svg'
import StripedChocolateIcon from '@/assets/icons/tiles/striped_chocolate.svg'

const props = defineProps<{
  active: boolean
}>()

const emit = defineEmits(['close'])

function close() {
  emit('close')
}

const { t } = useI18n()

const { popup, popupContent } = usePopup({ active: () => props.active, close })
</script>

<style lang="scss" scoped>
$close-size: var(--close-size);

.popup {
  --close-size: #{rem(64px)};
  --tile-size: #{rem(45px)};
  @include fill-space($position: fixed);
  inset: 0;
  z-index: z(popup);
  align-items: center;
  padding-top: $close-size;
  overflow-y: auto;
  background: $dark-brown-90;

  @include media-up(md) {
    --tile-size: #{rem(57px)};
  }

  @include media-up(lg) {
    --close-size: #{rem(57px)};
  }
}

.popup-inner {
  @include media-up(lg) {
    position: absolute;
    top: 50%;
    left: calc(50% + var(--close-size));
    display: flex;
    transform: translateX(-50%) translateY(-50%);
  }
}

.content {
  position: absolute;
  inset-inline: rem(24px);
  top: 50%;
  width: calc(100% - rem(24px * 2));
  margin-top: auto;
  margin-bottom: auto;
  overflow: hidden;
  color: $dark-brown;
  transform: translateY(-50%);

  @include media-up(md) {
    width: rem(382px);
    margin: auto;
  }

  @include media-up(lg) {
    position: relative;
    left: 0;
    width: rem(566px);
    margin: 0;
    transform: none;
  }
}

.close {
  @include size($close-size);
  position: fixed;
  top: 0;
  right: 0;
  z-index: z(popup) + 1;
  background: $orange;

  @include media-up(lg) {
    position: static;
  }
}

.close-icon {
  @include size(rem(18px));
  color: $beige;

  @include media-up(md) {
    @include size(rem(20px));
  }

  .contrast-mode & {
    color: $contrast-black;
  }
}

.content-bg-top,
.content-bg-bottom {
  display: grid;
  height: rem(210px);

  @include media-up(md) {
    height: rem(214px);
  }

  @include media-up(lg) {
    height: rem(128px);
  }
}

.bg1,
.bg2,
.bg3,
.bg4 {
  background-color: $beige;
}

.content-bg-top {
  grid-template-areas:
    '. icon1 bg1'
    'icon2 bg2 bg2'
    'bg3 bg3 bg3';
  grid-template-rows: repeat(2, var(--tile-size)) 1fr;
  grid-template-columns: repeat(2, var(--tile-size)) 1fr;

  .bg1,
  .bg2 {
    margin-bottom: -1px;
    margin-left: -1px;
  }

  .bg3 {
    margin-top: -1px;
  }
}

.content-bg-bottom {
  grid-template-areas:
    'bg1 bg1 bg1 bg1'
    'bg2 bg3 bg4 icon1'
    'bg2 icon2 . .';
  grid-template-rows: 1fr repeat(2, var(--tile-size));
  grid-template-columns: 1fr repeat(3, var(--tile-size));

  .bg1 {
    margin-top: -1px;
  }

  .bg2 {
    margin-top: -1px;
    margin-right: -1px;
  }

  .bg3,
  .bg4 {
    margin-top: -1px;
    margin-right: -1px;
  }
}

@each $key in (icon1, icon2, bg1, bg2, bg3, bg4) {
  .#{$key} {
    grid-area: $key;
  }
}

.icon1,
.icon2 {
  width: 100%;
  height: 100%;
}

.text {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  text-align: center;
  transform: translateX(-50%) translateY(-50%);
}

.title {
  margin-bottom: rem(27px);
  font-size: rem(44px);
  font-weight: 945;
  line-height: rem(29px);
  text-transform: uppercase;
  letter-spacing: -0.025em;
}

.description {
  @include font-size(
    (
      xs: 16px
    ),
    1.2
  );
  font-size: rem(16px);
  font-weight: 500;
  letter-spacing: -0.025em;
}
</style>
